/**
 * Driver Offline Reference Pack (Phase 13.8)
 *
 * Additive layer on top of the existing offline engine. Caches the small,
 * job-independent reference data a driver needs to *complete* work with no
 * signal (workflow configuration, permissions, appraisal reference data).
 *
 * Stored in the existing `app_state` key/value store — no schema changes,
 * no new IndexedDB databases, no changes to jobs_cache / sync_queue.
 *
 * Read path is offline-safe: `getDriverPack()` never touches the network.
 */

import { supabase } from "@/integrations/supabase/client";
import { appState } from "./offlineDb";

const PACK_KEY = "driver_offline_pack_v1";

export interface DriverOfflinePack {
  version: 1;
  user_id: string;
  company_id: string | null;
  driver_ids: string[];
  /** Driver row (permissions/flags the driver UI reads) */
  driver: Record<string, unknown> | null;
  /** Company workflow configuration required by driver screens */
  companySettings: Record<string, unknown> | null;
  /** Appraisal reference data (panel anchors used by damage marking) */
  vehiclePanelAnchors: unknown[];
  fetchedAt: number;
}

/** Read the cached pack. Never performs network I/O. */
export async function getDriverPack(): Promise<DriverOfflinePack | null> {
  try {
    const raw = await appState.get(PACK_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DriverOfflinePack;
  } catch {
    return null;
  }
}

/** Remove the cached pack (sign-out). */
export async function clearDriverPack(): Promise<void> {
  try {
    await appState.delete(PACK_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Refresh the reference pack from the server.
 * Returns the cached pack unchanged if it is younger than `maxAgeMs`.
 * Any failure leaves the previous cached pack intact.
 */
export async function refreshDriverPack(
  userId: string,
  maxAgeMs = 6 * 60 * 60 * 1000
): Promise<DriverOfflinePack | null> {
  const existing = await getDriverPack();
  if (
    existing &&
    existing.user_id === userId &&
    Date.now() - existing.fetchedAt < maxAgeMs
  ) {
    return existing;
  }

  try {
    const { data: drivers } = await supabase
      .from("drivers")
      .select("*")
      .eq("user_id", userId);

    const driverRows = drivers || [];
    const companyId =
      (driverRows[0]?.company_id as string | undefined) ??
      existing?.company_id ??
      null;

    let companySettings: Record<string, unknown> | null =
      existing?.companySettings ?? null;
    if (companyId) {
      const { data: settings } = await supabase
        .from("company_settings")
        .select("*")
        .eq("company_id", companyId)
        .maybeSingle();
      if (settings) companySettings = settings as Record<string, unknown>;
    }

    const { data: anchors } = await supabase
      .from("vehicle_panel_anchors")
      .select("*");

    const pack: DriverOfflinePack = {
      version: 1,
      user_id: userId,
      company_id: companyId,
      driver_ids: driverRows.map((d) => d.id as string),
      driver: (driverRows[0] as Record<string, unknown>) ?? existing?.driver ?? null,
      companySettings,
      vehiclePanelAnchors: anchors || existing?.vehiclePanelAnchors || [],
      fetchedAt: Date.now(),
    };

    await appState.set(PACK_KEY, JSON.stringify(pack));
    return pack;
  } catch (err) {
    console.warn("[DriverPack] Refresh failed (keeping cached pack):", err);
    return existing;
  }
}
